import type { FormDialogRef, RadioCard } from '@/components'
import {
  Button,
  Checkbox,
  FAIcon,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FormDialog,
  NumberInput,
  RadioCards,
  Radios,
} from '@/components'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import { useMutation, useQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Activity, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import z from 'zod'
import type {
  GenerationRuleCalculation,
  GenerationRuleKind,
  GenerationRuleRestHandling,
  GenerationRuleRounding,
} from './generation-rule-labels'
import {
  CHAIN_CALCULATIONS,
  useGenerationRuleLabels,
} from './generation-rule-labels'
import type { GenerationRulePreset } from './generation-rule-presets'
import {
  GENERATION_RULE_PRESETS,
  presetModifierInput,
  presetRuleValues,
} from './generation-rule-presets'
import {
  CREATE_SALARY_ITEM_GENERATION_MODIFIER_MUTATION,
  CREATE_SALARY_ITEM_GENERATION_RULE_MUTATION,
  PATCH_SALARY_ITEM_GENERATION_RULE_MUTATION,
} from './mutations'
import type { GenerationRuleNode } from './queries'
import {
  ALL_SALARY_ITEM_GENERATION_MODIFIERS_QUERY,
  DIET_SETTINGS_SALARY_WORK_TIMES_QUERY,
} from './queries'

function useGenerationRuleSchema() {
  const translations = useTranslate({ required: 'common.required' })

  return z
    .object({
      kind: z.enum(['diet', 'salary_day', 'both']),
      rule: z.enum(['per_work_period', 'six_hour_quarter', 'eight_hour_third']),
      dietSalaryCode: z.string(),
      salaryDaySalaryCode: z.string(),
      restHandling: z.enum(['break', 'continue']),
      amount: z.number().min(0),
      perDurationHours: z.number().positive().nullable(),
      rounding: z.enum(['floor', 'ceil']),
      isActive: z.boolean(),
    })
    .superRefine((values, context) => {
      if (values.kind !== 'salary_day' && !values.dietSalaryCode)
        context.addIssue({
          code: 'custom',
          path: ['dietSalaryCode'],
          message: translations.required,
        })
      if (values.kind !== 'diet' && !values.salaryDaySalaryCode)
        context.addIssue({
          code: 'custom',
          path: ['salaryDaySalaryCode'],
          message: translations.required,
        })
    })
}

export type GenerationRuleForm = z.infer<
  ReturnType<typeof useGenerationRuleSchema>
>

interface Props {
  salaryWorkTimeId: string
  /** The kinds this rule may take beside the salary setting's other rules. */
  kinds: GenerationRuleKind[]
  rule?: GenerationRuleNode
}

export function defaultValues(
  rule?: GenerationRuleNode,
  kind: GenerationRuleKind = 'diet'
): GenerationRuleForm {
  return {
    kind: (rule?.kind as GenerationRuleKind) ?? kind,
    rule: (rule?.rule as GenerationRuleCalculation) ?? 'per_work_period',
    dietSalaryCode: rule?.dietSalaryCode?.id ?? '',
    salaryDaySalaryCode: rule?.salaryDaySalaryCode?.id ?? '',
    restHandling: (rule?.restHandling as GenerationRuleRestHandling) ?? 'break',
    amount: rule?.amount ?? 1,
    perDurationHours: rule?.perDurationHours ?? null,
    rounding: (rule?.rounding as GenerationRuleRounding) ?? 'ceil',
    isActive: rule?.isActive ?? true,
  }
}

export function GenerationRuleDialog({ salaryWorkTimeId, kinds, rule }: Props) {
  const translations = useTranslate({
    add: 'settings.generation-rules.add',
    edit: 'settings.generation-rules.edit',
    kind: 'settings.generation-rules.kind',
    rule: 'settings.generation-rules.rule',
    dietSalaryCode: 'settings.generation-rules.diet-salary-code',
    salaryDaySalaryCode: 'settings.generation-rules.salary-day-salary-code',
    restHandling: 'settings.generation-rules.rest-handling',
    amount: 'settings.generation-rules.amount',
    perDurationHours: 'settings.generation-rules.per-duration-hours',
    perDurationHoursDescription:
      'settings.generation-rules.per-duration-hours-description',
    rounding: 'settings.generation-rules.rounding',
    active: 'common.active',
    next: 'common.next',
    back: 'common.back',
    modifierName: 'settings.generation-rules.presets.modifier-name',
    existingModifiers: [
      'settings.generation-rules.presets.existing-modifiers',
      { names: '' },
    ],
    modifierNote: [
      'settings.generation-rules.presets.modifier-note',
      { name: '' },
    ],
    presets: {
      custom: {
        title: 'settings.generation-rules.presets.custom.title',
        description: 'settings.generation-rules.presets.custom.description',
      },
      per_work_period: {
        title: 'settings.generation-rules.presets.per-work-period.title',
        description:
          'settings.generation-rules.presets.per-work-period.description',
      },
      per_work_period_rests: {
        title: 'settings.generation-rules.presets.per-work-period-rests.title',
        description:
          'settings.generation-rules.presets.per-work-period-rests.description',
      },
      six_hour: {
        title: 'settings.generation-rules.presets.six-hour.title',
        description: 'settings.generation-rules.presets.six-hour.description',
      },
      eight_hour: {
        title: 'settings.generation-rules.presets.eight-hour.title',
        description: 'settings.generation-rules.presets.eight-hour.description',
      },
    },
  })
  const labels = useGenerationRuleLabels()

  const dialogRef = useRef<FormDialogRef>(null)
  const [error, setError] = useState<string | null>(null)
  // Every preset is a diet rule, so they are offered only while diets are free.
  const firstPage = !rule && kinds.includes('diet') ? 'preset' : 'values'
  const [page, setPage] = useState<'preset' | 'values'>(firstPage)
  const [preset, setPreset] = useState<GenerationRulePreset>('custom')

  const form = useForm({
    resolver: zodResolver(useGenerationRuleSchema()),
    defaultValues: defaultValues(rule, kinds[0]),
    mode: 'onChange',
  })
  const kind = useWatch({ control: form.control, name: 'kind' })
  const calculation = useWatch({ control: form.control, name: 'rule' })
  const perDurationHours = useWatch({
    control: form.control,
    name: 'perDurationHours',
  })
  const isChainRule = CHAIN_CALCULATIONS.includes(calculation)

  const refetchQueries = [{ query: DIET_SETTINGS_SALARY_WORK_TIMES_QUERY }]
  const [createRule] = useMutation(
    CREATE_SALARY_ITEM_GENERATION_RULE_MUTATION,
    {
      refetchQueries,
      awaitRefetchQueries: true,
    }
  )
  const [patchRule] = useMutation(PATCH_SALARY_ITEM_GENERATION_RULE_MUTATION, {
    refetchQueries,
    awaitRefetchQueries: true,
  })
  const [createModifier] = useMutation(
    CREATE_SALARY_ITEM_GENERATION_MODIFIER_MUTATION,
    { refetchQueries: [{ query: ALL_SALARY_ITEM_GENERATION_MODIFIERS_QUERY }] }
  )
  const modifierInput = presetModifierInput(
    preset,
    salaryWorkTimeId,
    translations.modifierName
  )
  const { data: modifierData } = useQuery(
    ALL_SALARY_ITEM_GENERATION_MODIFIERS_QUERY,
    { skip: firstPage !== 'preset' }
  )
  const applyingModifiers = (
    modifierData?.allSalaryItemGenerationModifiers ?? []
  ).filter(
    modifier =>
      !modifier.salaryWorkTime ||
      modifier.salaryWorkTime.id === salaryWorkTimeId
  )
  const presetCards: RadioCard<GenerationRulePreset>[] =
    GENERATION_RULE_PRESETS.map(value => ({
      value,
      ...translations.presets[value],
    }))

  function applyPreset() {
    form.reset(presetRuleValues(preset, form.getValues()))
    setPage('values')
  }

  const kindOptions = kinds.map(value => ({
    value,
    label: labels.kind[value],
  }))
  const ruleOptions = (
    ['per_work_period', 'six_hour_quarter', 'eight_hour_third'] as const
  ).map(value => ({ value, label: labels.rule[value] }))
  const restHandlingOptions = (['break', 'continue'] as const).map(value => ({
    value,
    label: labels.restHandling[value],
  }))
  const roundingOptions = (['ceil', 'floor'] as const).map(value => ({
    value,
    label: labels.rounding[value],
  }))

  // Start over on every open, from the salary setting's rules as they are now:
  // creating or deleting a rule changes which kinds and presets apply.
  function startOver() {
    setError(null)
    form.reset(defaultValues(rule, kinds[0]))
    setPreset('custom')
    setPage(firstPage)
  }

  async function handleSubmit(values: GenerationRuleForm) {
    const writesDiets = values.kind !== 'salary_day'
    const writesSalaryDays = values.kind !== 'diet'
    const perWorkPeriod = values.rule === 'per_work_period'
    const input = {
      salaryWorkTime: salaryWorkTimeId,
      kind: values.kind,
      rule: values.rule,
      dietSalaryCode: writesDiets ? values.dietSalaryCode : null,
      salaryDaySalaryCode: writesSalaryDays ? values.salaryDaySalaryCode : null,
      restHandling: values.restHandling,
      amount: perWorkPeriod ? values.amount : 1,
      perDurationHours: perWorkPeriod ? values.perDurationHours : null,
      rounding: values.rounding,
      isActive: values.isActive,
    }
    try {
      if (rule) await patchRule({ variables: { id: rule.id, input } })
      else {
        await createRule({ variables: { input } })
        if (modifierInput)
          await createModifier({ variables: { input: modifierInput } })
      }
      dialogRef.current?.close()
    } catch (mutationError) {
      setError((mutationError as Error).message)
    }
  }

  return (
    <FormDialog
      ref={dialogRef}
      form={form}
      error={error}
      trigger={
        rule ? (
          <Button variant="ghost" size="icon-sm">
            <FAIcon icon="edit" />
          </Button>
        ) : (
          <Button size="sm">
            <FAIcon icon="plus" />
            {translations.add}
          </Button>
        )
      }
      className="sm:max-w-2xl"
      header={rule ? translations.edit : translations.add}
      prevButton={{
        show: page === 'values' && firstPage === 'preset',
        text: translations.back,
        onClick: () => setPage('preset'),
      }}
      nextButton={{
        show: page === 'preset',
        text: translations.next,
        onClick: applyPreset,
      }}
      onOpen={startOver}
      onSubmit={handleSubmit}
    >
      <Activity mode={page === 'preset' ? 'visible' : 'hidden'}>
        {applyingModifiers.length > 0 && (
          <p className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {translations.existingModifiers({
              names: applyingModifiers
                .map(modifier => modifier.name)
                .join(', '),
            })}
          </p>
        )}
        <RadioCards<GenerationRulePreset>
          value={preset}
          options={presetCards}
          onChange={setPreset}
        />
      </Activity>
      <Activity mode={page === 'values' ? 'visible' : 'hidden'}>
        <FieldGroup>
          {modifierInput && (
            <p className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-700">
              {translations.modifierNote({ name: translations.modifierName })}
            </p>
          )}
          <Controller
            control={form.control}
            name="kind"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.kind}</FieldLabel>
                <Radios
                  value={field.value}
                  options={kindOptions}
                  onChange={field.onChange}
                />
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="rule"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.rule}</FieldLabel>
                <Radios
                  value={field.value}
                  options={ruleOptions}
                  onChange={field.onChange}
                />
              </Field>
            )}
          />

          {kind !== 'salary_day' && (
            <Controller
              control={form.control}
              name="dietSalaryCode"
              render={({ field, fieldState }) => (
                <Field aria-invalid={fieldState.invalid}>
                  <FieldLabel>{translations.dietSalaryCode}</FieldLabel>
                  <SalaryCodeCombobox
                    mode="single"
                    value={field.value}
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          )}

          {kind !== 'diet' && (
            <Controller
              control={form.control}
              name="salaryDaySalaryCode"
              render={({ field, fieldState }) => (
                <Field aria-invalid={fieldState.invalid}>
                  <FieldLabel>{translations.salaryDaySalaryCode}</FieldLabel>
                  <SalaryCodeCombobox
                    mode="single"
                    value={field.value}
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          )}

          {isChainRule && (
            <Controller
              control={form.control}
              name="restHandling"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.restHandling}</FieldLabel>
                  <Radios
                    value={field.value}
                    options={restHandlingOptions}
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          )}

          {!isChainRule && (
            <>
              <Controller
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <Field>
                    <FieldLabel>{translations.amount}</FieldLabel>
                    <NumberInput
                      value={field.value}
                      decimalScale={3}
                      onChange={value => field.onChange(value ?? 0)}
                    />
                  </Field>
                )}
              />
              <Controller
                control={form.control}
                name="perDurationHours"
                render={({ field }) => (
                  <Field>
                    <FieldLabel>{translations.perDurationHours}</FieldLabel>
                    <FieldDescription>
                      {translations.perDurationHoursDescription}
                    </FieldDescription>
                    <NumberInput
                      value={field.value ?? undefined}
                      decimalScale={2}
                      onChange={value => field.onChange(value || null)}
                    />
                  </Field>
                )}
              />
              {!!perDurationHours && (
                <Controller
                  control={form.control}
                  name="rounding"
                  render={({ field }) => (
                    <Field>
                      <FieldLabel>{translations.rounding}</FieldLabel>
                      <Radios
                        value={field.value}
                        options={roundingOptions}
                        onChange={field.onChange}
                      />
                    </Field>
                  )}
                />
              )}
            </>
          )}

          <Controller
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <Field orientation="horizontal">
                <Checkbox {...field} id="generation-rule-active" size="md" />
                <FieldLabel htmlFor="generation-rule-active">
                  {translations.active}
                </FieldLabel>
              </Field>
            )}
          />
        </FieldGroup>
      </Activity>
    </FormDialog>
  )
}
